import * as React from 'react';
import { Calendar } from 'lucide-react';
import { Input } from './input';
import { Label } from './label';
import { cn } from '../../lib/utils';

export interface DateTimePickerProps
  extends Pick<React.AriaAttributes, 'aria-invalid' | 'aria-describedby'> {
  id?: string;
  label?: string;
  /** ISO-8601 string in the user's local zone. */
  value?: string;
  onChange?: (next: string) => void;
  min?: string;
  max?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * DateTime picker — wraps the native `datetime-local` input which is the
 * fastest path to a localized, RTL-correct, accessible picker. We do not
 * ship a custom calendar widget for V1 — operator workflows use UTC ISO
 * strings end-to-end and the native picker honours the OS locale.
 */
function DateTimePicker({
  id,
  label,
  value,
  onChange,
  min,
  max,
  required,
  disabled,
  className,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: DateTimePickerProps): React.JSX.Element {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <Label htmlFor={id} className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5" /> {label}
        </Label>
      )}
      <Input
        id={id}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        type="datetime-local"
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        min={min ?? ''}
        max={max ?? ''}
        required={required ?? false}
        disabled={disabled ?? false}
      />
    </div>
  );
}

export { DateTimePicker };
